import React from 'react';
import SearchableAccordion, { AccordionCategory } from './SearchableAccordion';

const HELP_DATA: AccordionCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'BookOpen',
    items: [
      {
        id: 'dashboard',
        title: 'Dashboard',
        content: 'The Dashboard allows you to quickly review your progress at various goals and continue any training you may have started previously.',
      },
      {
        id: 'prompt-challenges',
        title: 'Prompt Challenges',
        content: 'Prompt Challenges are the primary training component and give you the scenario on which to try writing an effective prompt. We recommend beginners start with the Basic Prompt Training before proceeding to specific topics. Higher difficulty challenges can be unlocked.',
      },
      {
        id: 'prompt-quest',
        title: 'Prompt Quest',
        content: 'Try a fun role-playing quest to test your prompting skills! Proceed through a story based on your responses and prompt your way through progressively harder circumstances.',
      },
      {
        id: 'playground',
        title: 'Playground',
        content: 'A simple space to test any prompt, including some example prompts. More advanced settings options allow the user to vary the underlying model parameters and test AI concepts.',
      },
      {
        id: 'reference',
        title: 'Reference',
        content: 'We recommend new users start here. You can review common terminology and read about various prompting techniques to help you get started on the challenges or improve your scores!',
      },
      {
        id: 'badges-rewards',
        title: 'Badges & Rewards',
        content: 'Earn fun icons as you strengthen your AI skills and check your progress on various achievements.',
      },
      {
        id: 'leaderboard',
        title: 'Leaderboard',
        content: 'Compete with other users for the top spot!',
      },
      {
        id: 'profile',
        title: 'Profile',
        content: 'View your training progress, skills, and mission history.',
      },
    ],
  },
  {
    id: 'faq',
    title: 'Frequently Asked Questions',
    icon: 'CircleHelp',
    items: [
      {
        id: 'purpose',
        title: 'What is the purpose of this application?',
        content: 'AI and generative AI tools are becoming part of everyday work, but writing prompts that actually get good results is a real skill. Whiskey Sensei gives you an opportunity to learn to write effective and efficient prompts in a fun and engaging way. The hope is that this "gamification" of prompt engineering leads to increased engagement and confidence using these tools, a shorter learning curve, and better results from the models you work with.',
      },
      {
        id: 'who-for',
        title: 'Who is this application for?',
        content: 'Anyone! AI tools can be used by anyone in any role for a vast number of use cases, so our training tool takes a similar approach. Learn to write effective prompts and apply your learning to a variety of realistic scenarios, from everyday work tasks to agentic-framework fundamentals. Gamification concepts like badges and streaks motivate users to keep exploring.',
      },
      {
        id: 'how-built',
        title: 'How was this application built?',
        content: 'Whiskey Sensei is a cloud-native, gamified prompt engineering learning platform built entirely on AWS. The platform features a React/TypeScript frontend hosted on CloudFront/S3, a serverless Node.js backend via Lambda and API Gateway, AI feedback through Amazon Bedrock (Claude Sonnet 4.5), and DynamoDB for data storage. The architecture includes security through JWT authentication, least-privilege IAM roles, and Bedrock Guardrails for content moderation, deployed via Terraform Infrastructure as Code.',
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: 'Wrench',
    items: [
      {
        id: 'inconsistent-output',
        title: 'Why is my output inconsistent?',
        content: 'High temperature settings cause variability. Lower the temperature (0.0-0.3) for more consistent results. Also ensure your prompt is specific and unambiguous.',
      },
      {
        id: 'reduce-hallucinations',
        title: 'How to reduce hallucinations?',
        content: 'Hallucinations (made-up information) can be reduced by: (1) Being very specific in your prompt, (2) Asking the model to cite sources or admit uncertainty, (3) Using lower temperature, (4) Providing relevant context.',
      },
      {
        id: 'long-documents',
        title: 'How to handle long documents?',
        content: 'For documents exceeding the context window: (1) Summarize sections first, (2) Use prompt chaining to process chunks, (3) Extract key information before analysis, (4) Use retrieval-augmented generation (RAG) techniques.',
      },
      {
        id: 'improve-quality',
        title: 'How to improve response quality?',
        content: 'Key strategies: (1) Add more context and examples, (2) Use chain-of-thought prompting, (3) Specify output format clearly, (4) Iterate and refine your prompt, (5) Break complex tasks into steps.',
      },
      {
        id: 'temperature-guide',
        title: 'When to use higher/lower temperature?',
        content: 'Low (0.0-0.3): Factual tasks, data extraction, classification, code generation. Medium (0.4-0.7): General conversation, explanations, balanced creativity. High (0.8-1.0): Creative writing, brainstorming, diverse ideas.',
      },
    ],
  },
];

const HelpFAQ: React.FC = () => (
  <SearchableAccordion
    heading="Help & FAQ"
    subheading="Learn how to use the platform and find answers to common questions."
    searchPlaceholder="Search for help topics, questions, or troubleshooting…"
    categories={HELP_DATA}
    defaultExpandedCategory="getting-started"
  />
);

export default HelpFAQ;
